import React, { useCallback, useEffect, useMemo, useState } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import { withDirectusAssetTransformKey } from "../../../routes/pricing-reports/pricingReportImageUrls";
import {
  LABEL_ANALYSIS_BOOLEAN_FILTERS,
  LABEL_ANALYSIS_DEFAULT_SORT,
  fetchAveragePriceByCountryOrigin,
  fetchLabelAnalysisFilterOptions,
  fetchLabelAnalysisRows,
  getLabelAnalysisSortUi,
  labelAnalysisConfigured,
  nextLabelAnalysisSortBy,
} from "../../../services/labelAnalysis";
import LocationLabelDetailDialog from "./LocationLabelDetailDialog";
import LocationLabelOriginPriceChart from "./LocationLabelOriginPriceChart";
import LocationLabelToolbar from "./LocationLabelToolbar";
import LocationLabelTriStateChip from "./LocationLabelTriStateChip";

const EMPTY_BOOLEAN_FILTERS = LABEL_ANALYSIS_BOOLEAN_FILTERS.reduce((acc, filter) => {
  acc[filter.key] = "all";
  return acc;
}, {});

const TABLE_COLUMN_COUNT = 7 + LABEL_ANALYSIS_BOOLEAN_FILTERS.length;

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

function formatDateFromForApi(value) {
  return value?.isValid?.() ? value.format("YYYY-MM-DD") : "";
}

function formatDateToForApi(value) {
  return value?.isValid?.() ? `${value.format("YYYY-MM-DD")}T23:59:59.999` : "";
}

function isDateRangeValid(dateFrom, dateTo) {
  const from = formatDateFromForApi(dateFrom);
  const to = formatDateToForApi(dateTo);
  if (!from || !to) {
    return true;
  }
  return from <= to.slice(0, 10);
}

const SortableHeaderCell = ({ columnId, label, orderBy, order, onSort }) => (
  <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === columnId ? order : false}>
    <TableSortLabel
      active={orderBy === columnId}
      direction={orderBy === columnId ? order : "asc"}
      onClick={() => onSort(columnId)}
    >
      {label}
      {orderBy === columnId ? (
        <Box component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </Box>
      ) : null}
    </TableSortLabel>
  </TableCell>
);

const LocationLabelReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [countryOrigin, setCountryOrigin] = useState("");
  const [grade, setGrade] = useState("");
  const [quality, setQuality] = useState("");
  const [product, setProduct] = useState("");
  const [retailer, setRetailer] = useState("");
  const [booleanFilters, setBooleanFilters] = useState(EMPTY_BOOLEAN_FILTERS);
  const [sortBy, setSortBy] = useState(LABEL_ANALYSIS_DEFAULT_SORT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    grades: [],
    qualities: [],
    products: [],
    retailers: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [originPriceRows, setOriginPriceRows] = useState([]);
  const [originPriceLoading, setOriginPriceLoading] = useState(false);
  const [originPriceError, setOriginPriceError] = useState("");

  const resetPage = (updater) => (nextValue) => {
    setPage(0);
    updater(nextValue);
  };

  const handleBooleanFilterChange = (key, nextValue) => {
    setPage(0);
    setBooleanFilters((current) => ({ ...current, [key]: nextValue }));
  };

  const dateRangeIsValid = isDateRangeValid(dateFrom, dateTo);

  const listFilters = useMemo(() => {
    return {
      countryOrigin,
      grade,
      quality,
      product,
      retailer,
      ...booleanFilters,
      dateFrom: dateRangeIsValid ? formatDateFromForApi(dateFrom) : "",
      dateTo: dateRangeIsValid ? formatDateToForApi(dateTo) : "",
    };
  }, [booleanFilters, countryOrigin, dateFrom, dateRangeIsValid, dateTo, grade, product, quality, retailer]);

  const loadFilterOptions = useCallback(async () => {
    if (!labelAnalysisConfigured()) {
      return;
    }

    try {
      const nextOptions = await fetchLabelAnalysisFilterOptions();
      setFilterOptions(nextOptions);
    } catch (_) {
      setFilterOptions({
        countries: [],
        grades: [],
        qualities: [],
        products: [],
        retailers: [],
      });
    }
  }, []);

  const loadData = useCallback(async () => {
    if (!labelAnalysisConfigured()) {
      setError(
        "Location label report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
      );
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await fetchLabelAnalysisRows({
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        ...listFilters,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(e.message || "Failed to load label analysis records.");
    } finally {
      setLoading(false);
    }
  }, [listFilters, page, rowsPerPage, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    let active = true;

    async function loadOriginPriceChart() {
      if (!product || !labelAnalysisConfigured()) {
        if (active) {
          setOriginPriceRows([]);
          setOriginPriceError("");
          setOriginPriceLoading(false);
        }
        return;
      }

      setOriginPriceLoading(true);
      setOriginPriceError("");
      try {
        const nextRows = await fetchAveragePriceByCountryOrigin(listFilters);
        if (!active) {
          return;
        }
        setOriginPriceRows(nextRows);
      } catch (e) {
        if (!active) {
          return;
        }
        setOriginPriceRows([]);
        setOriginPriceError(e.message || "Failed to load origin price chart.");
      } finally {
        if (active) {
          setOriginPriceLoading(false);
        }
      }
    }

    loadOriginPriceChart();
    return () => {
      active = false;
    };
  }, [listFilters, product]);

  const sortUi = getLabelAnalysisSortUi(sortBy);

  const handleSort = (columnId) => {
    setPage(0);
    setSortBy(nextLabelAnalysisSortBy(columnId, sortBy));
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
        Location / Label
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review package-label analysis from store photos. Click a row to open the associated image.
      </Typography>

      {!labelAnalysisConfigured() ? (
        <Alert severity="warning">Beef AI Directus env vars are not configured for this report.</Alert>
      ) : null}

      <LocationLabelToolbar
        dateFrom={dateFrom}
        onDateFromChange={resetPage(setDateFrom)}
        dateTo={dateTo}
        onDateToChange={resetPage(setDateTo)}
        countryOrigin={countryOrigin}
        onCountryOriginChange={resetPage(setCountryOrigin)}
        countryOptions={filterOptions.countries}
        grade={grade}
        onGradeChange={resetPage(setGrade)}
        gradeOptions={filterOptions.grades}
        quality={quality}
        onQualityChange={resetPage(setQuality)}
        qualityOptions={filterOptions.qualities}
        product={product}
        onProductChange={resetPage(setProduct)}
        productOptions={filterOptions.products}
        retailer={retailer}
        onRetailerChange={resetPage(setRetailer)}
        retailerOptions={filterOptions.retailers}
        booleanFilters={booleanFilters}
        onBooleanFilterChange={handleBooleanFilterChange}
      />

      {!dateRangeIsValid ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Select a valid date range. From must be on or before To.
        </Alert>
      ) : null}

      {product ? (
        <LocationLabelOriginPriceChart
          productName={product}
          rows={originPriceRows}
          isLoading={originPriceLoading}
          error={originPriceError}
        />
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ position: "relative" }}>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading records...</Typography>
          </Stack>
        ) : null}

        <Box sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              width: "100%",
              border: "1px solid #eaeaea",
              backgroundColor: "#fff",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Label image</TableCell>
                <SortableHeaderCell
                  columnId="product"
                  label="Product"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
                <SortableHeaderCell
                  columnId="retailer"
                  label="Retailer"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
                <SortableHeaderCell
                  columnId="country_origin"
                  label="Country"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
                <SortableHeaderCell
                  columnId="grade"
                  label="Grade"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
                <SortableHeaderCell
                  columnId="quality"
                  label="Quality"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
                {LABEL_ANALYSIS_BOOLEAN_FILTERS.map((filter) => (
                  <SortableHeaderCell
                    key={filter.field}
                    columnId={filter.field}
                    label={filter.label}
                    orderBy={sortUi.id}
                    order={sortUi.direction}
                    onSort={handleSort}
                  />
                ))}
                <SortableHeaderCell
                  columnId="date_created"
                  label="Created"
                  orderBy={sortUi.id}
                  order={sortUi.direction}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const coverUrl = row.images[0]
                  ? withDirectusAssetTransformKey(row.images[0])
                  : "";

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelected(row)}
                  >
                    <TableCell>
                      {coverUrl ? (
                        <Box
                          component="img"
                          src={coverUrl}
                          alt=""
                          sx={{
                            width: 72,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid #ddd",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.productName}</TableCell>
                    <TableCell>{row.retailerLabel}</TableCell>
                    <TableCell>{row.countryLabel}</TableCell>
                    <TableCell>{row.grade || "-"}</TableCell>
                    <TableCell>{row.quality || "-"}</TableCell>
                    {LABEL_ANALYSIS_BOOLEAN_FILTERS.map((filter) => (
                      <TableCell key={filter.key}>
                        <LocationLabelTriStateChip value={row[filter.key]} />
                      </TableCell>
                    ))}
                    <TableCell>{formatDate(row.dateCreated)}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMN_COUNT}>
                    <Typography variant="body2">No label analysis records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>

      <LocationLabelDetailDialog
        open={Boolean(selected)}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};

export default LocationLabelReport;
